/**
 * Prediction Engine V2 — Edge Cases
 *
 * Handles new drivers, team changes, and confidence scaling
 * based on available data volume.
 */

import type { TeamCalculatorDriver } from "../algorithms/types";

/**
 * Get a baseline prediction for a new driver using teammate data.
 * Returns the teammate's average total points with a rookie discount.
 */
export function getNewDriverBaseline(
  constructorSlug: string,
  allDrivers: TeamCalculatorDriver[],
  currentDriverSlug: string,
): number {
  const teammates = allDrivers.filter(
    (d) =>
      d.constructorSlug === constructorSlug &&
      d.driverSlug !== currentDriverSlug &&
      d.raceHistory.length > 0,
  );

  if (teammates.length === 0) {
    // No teammate data — use a generic mid-field estimate
    return 12;
  }

  // Average teammate's recent total points
  const teammateAvg =
    teammates.reduce((sum, t) => {
      const pts = t.raceHistory.map((r) => r.totalPoints);
      return sum + pts.reduce((a, b) => a + b, 0) / pts.length;
    }, 0) / teammates.length;

  // Rookie discount: 80% of teammate performance
  return teammateAvg * 0.8;
}

/**
 * Get confidence score based on the amount of historical data available.
 */
export function getConfidenceForDataVolume(raceCount: number): number {
  if (raceCount === 0) return 0.2;
  if (raceCount === 1) return 0.3;
  if (raceCount <= 3) return 0.5;
  if (raceCount <= 5) return 0.7;
  if (raceCount <= 10) return 0.85;
  return 0.9;
}

/**
 * Get the default qualifying position estimate for a driver with no history.
 * Based on their constructor's typical performance.
 */
export function getDefaultQualiPosition(
  constructorSlug: string,
  allDrivers: TeamCalculatorDriver[],
  currentDriverSlug: string,
): number {
  const teammates = allDrivers.filter(
    (d) =>
      d.constructorSlug === constructorSlug &&
      d.driverSlug !== currentDriverSlug &&
      d.raceHistory.length > 0,
  );

  if (teammates.length === 0) return 12; // Mid-field default

  const teammateQualis = teammates.flatMap((t) =>
    t.raceHistory
      .map((r) => r.qualifyingPosition)
      .filter((p): p is number => p !== null),
  );

  if (teammateQualis.length === 0) return 12;

  const avg = teammateQualis.reduce((a, b) => a + b, 0) / teammateQualis.length;
  // Rookie penalty: 2 positions worse than teammate
  return Math.min(avg + 2, 20);
}
